import React, { useState, useEffect } from 'react'

import { v4 as uuidv4 } from 'uuid'

import {
    Box,
    Typography,
    TextField,
    IconButton,
    Tab,
    Tabs,
    Chip,
    Select,
    MenuItem,
    FormControl
} from '@mui/material'

import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'

import { ReactComponent as EmptyContentIcon } from '../../../icons/EmptyContentIcon.svg'

import { useDebouncedCallback } from '../../../hooks/useDebounce'

import Dropdown from '../../../components/Dropdown'
import { SecondaryButtonFactory } from '../../../components/Buttons/SecondaryButton'
import ArtosToggle from '../../../components/inputs/ArtosToggle'
import AsynchronousActionButton from '../../../components/Buttons/AsynchronousActionButton'

import { useInstructionalMRT } from '../../../contexts/InstructionalMRTContext'

export const EditTemplateSectionContent = ({
    section,
    onUpdate,
    hasToggledContent,
    setHasToggledContent
}) => {
    console.log('EditTemplateSectionContent received section:', section)
    console.log('section?.is_repeating', section?.is_repeating)

    const [localSection, setLocalSection] = useState(section)
    const [repeatSection, setRepeatSection] = useState(section?.is_repeating === true ? true : false)
    const [assistWithAI, setAssistWithAI] = useState(section?.section_mode === 'dynamic' ? true : false)
    const [useSourceData, setUseSourceData] = useState(section?.content_mode === 'data_driven' || section?.content_mode === 'data_driven' ? true : false)
    const [sectionFormat, setSectionFormat] = useState(0)
    const [allowedDataRules, setAllowedDataRules] = useState(section?.allowed_data_rules || [])
    // Local state for source selections (not saved to section object)
    const [sourceMappings, setSourceMappings] = useState({})
    
    // Check if section has meaningful content
    const hasContent = section?.section_mode === 'dynamic' ||
        (section?.rules && section.rules.length > 0 &&
         section.rules.some(rule => {
            const userInstructions = rule.user_instructions || ''
            const instructions = rule.instructions || ''
            // Section has content if it does NOT contain placeholder text in either field
            const hasPlaceholder =
                userInstructions.includes('Content for section') ||
                userInstructions.includes('was not generated per users request') ||
                instructions.includes('Content for section') ||
                instructions.includes('was not generated per users request')
            return !hasPlaceholder
         }) && section?.section_mode !== 'fixed')
    
    const [contentEnabled, setContentEnabled] = useState(hasContent)
    const [loading, setLoading] = useState(false)
    const { originalSection, saveChanges, hasChanges } = useInstructionalMRT()

    console.log('repeatSection', repeatSection)
    console.log('section?.is_repeating === true ? true : false', section?.is_repeating === true ? true : false)
    console.log('hasContent', hasContent)

    useEffect(() => {
        console.log('Section updated in EditTemplateSectionContent:', section)
        setLocalSection(section)
        setRepeatSection(section?.is_repeating === true ? true : false)
        setAllowedDataRules(section?.allowed_data_rules || [])

        // Initialize source mappings from existing user_instructions
        if (section?.rules && section.rules.length > 0) {
            const newMappings = {}
            section.rules.forEach((rule, index) => {
                const ruleId = rule.id || `rule_${index}`
                const match = rule.user_instructions?.match(/^Copy from (Protocol|SAP|TFL)\.?\s*/i)
                if (match) {
                    newMappings[ruleId] = match[1]
                }
            })
            setSourceMappings(newMappings)
        }

        // Check if section has meaningful content
        const hasContent = section?.section_mode === 'dynamic' ||
            (section?.rules && section.rules.length > 0 &&
             section.rules.some(rule => {
                const userInstructions = rule.user_instructions || ''
                const instructions = rule.instructions || ''
                // Section has content if it does NOT contain placeholder text in either field
                const hasPlaceholder =
                    userInstructions.includes('Content for section') ||
                    userInstructions.includes('was not generated per users request') ||
                    instructions.includes('Content for section') ||
                    instructions.includes('was not generated per users request')
                return !hasPlaceholder
             }))

        console.log('hasContent2', hasContent)

        setContentEnabled(hasContent)

        // Initialize with sample data if no rules exist and content is enabled
        if (hasContent && (!section?.rules || section.rules.length === 0)) {
            const sampleRules = [
                {
                    id: uuidv4(),
                    rule_type: 'copy_paste',
                    rule_mode: 'fixed',
                    user_instructions: 'Tell us how you\'d like information generated',
                    modify_with_ai: true
                },
                {
                    id: uuidv4(),
                    rule_type: 'template_text',
                    rule_mode: 'fixed',
                    user_instructions: 'Use "This section summarises the prior human clinical experience with [DRUG NAME], integrating data from completed and ongoing clinical studies as of the data-cut-off date."',
                    modify_with_ai: true
                },
                {
                    id: uuidv4(),
                    rule_type: 'copy_paste',
                    rule_mode: 'fixed',
                    user_instructions: 'Pull the exact official wording for the investigational product name and its active ingredient(s) from the latest approved source document',
                    modify_with_ai: true
                }
            ]
            setLocalSection({ ...section, rules: [] })
        }
    }, [section])

    const debouncedUpdate = useDebouncedCallback((updates) => {
        onUpdate(updates)
    }, 500)
    
    const handleContentToggle = () => {
        const newValue = !contentEnabled
        const newSectionMode = newValue ? 'dynamic' : 'fixed'
        const firstRuleId = originalSection?.rules?.[0]?.rule_id || uuidv4()
        
        let newRules = newValue
            ? (originalSection?.rules || [
                {
                    rule_id: firstRuleId,
                    rule_type: 'copy_paste',
                    rule_mode: 'fixed',
                    user_instructions: 'Tell us how you\'d like information generated',
                    modifications: null
                }
              ])
            : [{
                "rule_id": firstRuleId,
                "rule_type": "template_text",
                "rule_mode": "fixed",
                "user_instructions": `Only include the following text in this section: 'Content for section ${section.section_name} was not generated per users request'`,
                "modifications": null,
              }]
        
        setContentEnabled(newValue)
        setLocalSection({ ...localSection, section_mode: newSectionMode, rules: newRules })
        debouncedUpdate({ section_mode: newSectionMode, rules: newRules })
        
        // Track toggled content
        if (setHasToggledContent) {
            setHasToggledContent({
                ...hasToggledContent,
                [section.section_id || section.id]: true
            })
        }
    }

    const handleRepeatToggle = () => {
        const newValue = !repeatSection
        setRepeatSection(newValue)
        debouncedUpdate({ is_repeating: newValue })
    }

    const handleUseSourceDataToggle = () => {
        const newValue = !useSourceData
        if (newValue) {
            debouncedUpdate({ content_mode: 'data-driven' })
        } else {
            debouncedUpdate({ content_mode: 'background' })
        }
        setUseSourceData(newValue)
    }

    const handleAllowedDataRulesChange = (event) => {
        const value = event.target.value
        setAllowedDataRules(value)

        // If adding allowed data rules, automatically set content_mode to 'data_driven'
        // If removing all rules, set it back to 'background'
        if (value.length > 0) {
            console.log('✅ Allowed data rules added - setting content_mode to data_driven')
            setUseSourceData(true)
            debouncedUpdate({
                allowed_data_rules: value,
                content_mode: 'data_driven'
            })
        } else {
            console.log('❌ All allowed data rules removed - setting content_mode to background')
            setUseSourceData(false)
            debouncedUpdate({
                allowed_data_rules: value,
                content_mode: 'background'
            })
        }
    }

    const handleAssistToggle = () => {
        console.log('!assistWithAI', !assistWithAI)
        if (!assistWithAI === true) {
            debouncedUpdate({ section_mode: 'dynamic' })
        } else {
            debouncedUpdate({ section_mode: 'fixed' })
        }
        setAssistWithAI(!assistWithAI)
    }

    const handleFormatChange = (_, newValue) => {
        setSectionFormat(newValue)
    }

    const handleRuleChange = (index, field, value) => {
        const updatedRules = [...(localSection.rules || [])]

        // If rule_type is being changed, automatically set rule_mode based on the selected rule
        if (field === 'rule_type') {
            const fixedModeRules = ['literal_content', 'template_text', 'copy_paste', 'table_summarize']
            const newRuleMode = fixedModeRules.includes(value) ? 'fixed' : 'dynamic'

            // Clear source_type if changing away from copy_paste or modified_copy_paste
            const shouldClearSource = (value !== 'copy_paste' && value !== 'modified_copy_paste')

            // Handle special rule type conversions:
            // - fill_in_blank → template_text with dynamic mode
            // - modified_copy_paste → copy_paste with dynamic mode
            let actualRuleType = value
            let actualRuleMode = newRuleMode

            if (value === 'fill_in_blank') {
                actualRuleType = 'template_text'
                actualRuleMode = 'dynamic'
            } else if (value === 'modified_copy_paste') {
                actualRuleType = 'copy_paste'
                actualRuleMode = 'dynamic'
            }

            console.log('🔧 Rule type change:', value)
            console.log('  → Actual rule_type stored:', actualRuleType)
            console.log('  → rule_mode:', actualRuleMode)

            updatedRules[index] = {
                ...updatedRules[index],
                rule_type: actualRuleType,
                rule_mode: actualRuleMode,
                // Store the original selection for UI display
                ui_rule_type: value,
                ...(shouldClearSource && { source_type: undefined }),
                // Clear data source parameters if changing away from populate_table or fill_in_blank
                ...((value !== 'populate_table' && value !== 'fill_in_blank') && {
                    parameters: {
                        ...(updatedRules[index].parameters || {}),
                        data_source: undefined,
                        database_query: undefined
                    }
                })
            }

            // Clear "Copy from X" prefix from instructions if changing away from copy/paste rules
            if (shouldClearSource && updatedRules[index].user_instructions) {
                updatedRules[index].user_instructions = updatedRules[index].user_instructions
                    .replace(/^Copy from (Protocol|SAP|TFL)\\.?\\s*/i, '')
            }
        } else {
            updatedRules[index] = {
                ...updatedRules[index],
                [field]: value
            }
        }

        setLocalSection({ ...localSection, rules: updatedRules })
        debouncedUpdate({ rules: updatedRules })
    }

    const handleSectionChange = (field, value) => {
        setLocalSection({ ...localSection, [field]: value })
        debouncedUpdate({ [field]: value })
    }

    const handleCustomSectionChange = (value) => {
        handleSectionChange('template_instructions', value)
    }

    const handleAddRule = () => {
        const newRule = {
            rule_id: uuidv4(),
            rule_type: 'copy_paste',
            rule_mode: 'fixed', // Default to 'fixed' for copy_paste
            user_instructions: '',
            modifications: null
        }
        const updatedRules = [...(localSection.rules || []), newRule]
        setLocalSection({ ...localSection, rules: updatedRules })
        debouncedUpdate({ rules: updatedRules })
    }

    const handleDeleteRule = (index) => {
        const updatedRules = [...(localSection.rules || [])]
        updatedRules.splice(index, 1)
        setLocalSection({ ...localSection, rules: updatedRules })
        debouncedUpdate({ rules: updatedRules })
    }

    // Add handler for source selection in Copy/Paste rules
    const handleSourceChange = (index, sourceType) => {
        const rule = localSection.rules[index]
        const ruleId = rule.id || `rule_${index}`

        // Update local source mappings
        setSourceMappings(prev => ({
            ...prev,
            [ruleId]: sourceType
        }))

        // Update user_instructions with the selected source
        const updatedRules = [...(localSection.rules || [])]
        const currentInstructions = updatedRules[index].user_instructions || ''

        // Remove any existing "Copy from" prefix
        const cleanedInstructions = currentInstructions.replace(/^Copy from (Protocol|SAP|TFL)\.?\s*/i, '')

        // Add new prefix if source is selected
        const newInstructions = sourceType
            ? `Copy from ${sourceType}. ${cleanedInstructions}`.trim()
            : cleanedInstructions

        updatedRules[index] = {
            ...updatedRules[index],
            user_instructions: newInstructions
        }

        setLocalSection({ ...localSection, rules: updatedRules })
        debouncedUpdate({ rules: updatedRules })
    }

    const handleSave = async () => {
        setLoading(true)
        try {
            await saveChanges()
        } catch (error) {
            console.error("Error saving changes: ", error)
        } finally {
            setLoading(false)
        }
    }

    if (!section) {
        console.log('EditTemplateSectionContent: No section provided, returning null')
        return null
    }

    const ruleTypeOptions = [
        { value: 'literal_content', label: 'Use boilerplate text' },
        { value: 'template_text', label: 'Use template text' },
        { value: 'fill_in_blank', label: 'Fill-in-the-blank' },
        // { value: 'multi_select', label: 'Multi-select' },
        { value: 'copy_paste', label: 'Copy/Paste' },
        { value: 'modified_copy_paste', label: 'Modified Copy/Paste' },
        { value: 'summarize_content', label: 'Summarize' },
        { value: 'intra_document_summarize', label: 'Create intra-document summary' },
        { value: 'table_summarize', label: 'Create intra-document table' },
        { value: 'template_table', label: 'Template Table' }
    ]

    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '24px', 
            padding: '20px 0px 37px 0px',
            width: '100%',
            // minWidth: '500px',
            boxSizing: 'border-box'
        }}>
            {/* Section Title with Content Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 600,
                        fontSize: '24px',
                        lineHeight: '32px',
                        color: '#111827'
                    }}
                >
                    {section.title || section.section_name || 'Untitled Section'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Typography
                        sx={{
                            fontSize: '12px',
                            color: '#6b7280',
                            lineHeight: '20px',
                            fontWeight: 500
                        }}
                    >
                        Content
                    </Typography>
                    <Box
                        onClick={handleContentToggle}
                        sx={{
                            position: 'relative',
                            width: '44px',
                            height: '24px',
                            borderRadius: '12px',
                            backgroundColor: contentEnabled ? '#4F46E5' : '#E5E7EB',
                            padding: '2px',
                            cursor: 'pointer',
                            transition: 'background-color 0.3s ease',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <Box
                            sx={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '10px',
                                backgroundColor: '#FFFFFF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transform: contentEnabled ? 'translateX(20px)' : 'translateX(0px)',
                                transition: 'transform 0.3s ease',
                                padding: '4px',
                                boxSizing: 'border-box'
                            }}
                        >
                            {contentEnabled ? (
                                <CheckIcon 
                                    sx={{ 
                                        fontSize: '12px', 
                                        color: '#9CA3AF',
                                        width: '12px',
                                        height: '12px'
                                    }} 
                                />
                            ) : (
                                <ClearIcon 
                                    sx={{ 
                                        fontSize: '12px', 
                                        color: '#9CA3AF',
                                        width: '12px',
                                        height: '12px'
                                    }} 
                                />
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Content Area - Show either regular content or empty state */}
            {contentEnabled ? (
            <>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '16px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        gap: '16px',
                        boxSizing: 'border-box',
                        minWidth: 0,
                        width: '100%'
                    }}
                >
                    {/* Section Format */}
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '16px', 
                            width: '100%',
                            minWidth: 0
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                minWidth: 0,
                                backgroundColor: '#F5F5F5',
                                borderRadius: '8px',
                                padding: '2px',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >   
                            {/* Tabs */}
                            <Tabs
                                value={sectionFormat}
                                onChange={handleFormatChange}
                                aria-label="Section format"
                                slotProps={{ indicator: { style: { display: 'none' } } }}
                                variant="fullWidth"
                                sx={{
                                    width: '100%',
                                    height: '32px',
                                    padding: '2px',
                                    minHeight: 'unset',
                                    position: 'relative',
                                    zIndex: 2,
                                    borderBottom: 'none !important',
                                    backgroundColor: '#F3F4F6',
                                    '&::before': {
                                        display: 'none !important',
                                        borderBottom: 'none !important'
                                    },
                                    '&.MuiTabs-root': {
                                        borderBottom: 'none !important',
                                        '&::before': {
                                            display: 'none !important'
                                        }
                                    },
                                    '& .MuiTabs-scroller': {
                                        borderBottom: 'none !important',
                                        '&::before': {
                                            display: 'none !important',
                                            borderBottom: 'none !important'
                                        },
                                        '&::after': {
                                            display: 'none !important',
                                            borderBottom: 'none !important'
                                        }
                                    },
                                    '& .MuiTabs-flexContainer': {
                                        width: '100%',
                                        display: 'flex',
                                        gap: '4px',
                                        backgroundColor: '#F3F4F6',
                                        borderBottom: 'none !important',
                                        '&::before': {
                                            display: 'none !important',
                                            borderBottom: 'none !important'
                                        },
                                        '&::after': {
                                            display: 'none !important',
                                            borderBottom: 'none !important'
                                        }
                                    },
                                    '& .MuiTab-root': {
                                        flex: 1,
                                        minHeight: 'unset',
                                        padding: '9px 25px',
                                        color: '#374151',
                                        fontSize: '14px',
                                        lineHeight: '20px',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: '6px',
                                        transition: 'color 0.2s ease, font-weight 0.2s ease',
                                        backgroundColor: 'transparent',
                                        position: 'relative',
                                        zIndex: 2,
                                        height: '28px',
                                        '&:hover': {
                                            backgroundColor: 'transparent'
                                        }
                                    },
                                    '& .MuiTab-root.Mui-selected': {
                                        color: '#111827',
                                        lineHeight: '20px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        backgroundColor: '#FFFFFF',
                                        border: '1px solid #E5E7EB',
                                        boxSizing: 'border-box',
                                        boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)'
                                    }
                                }}
                            >
                                <Tab label="Section format" />
                                <Tab label="Custom" />
                            </Tabs>
                        </Box>
                    </Box>

                    {/* Toggle Options */}
                    {sectionFormat === 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            {/* Repeat Section Toggle */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                lineHeight: '20px',
                                                fontWeight: 500,
                                                color: '#374151',
                                                mb: '4px'
                                            }}
                                        >
                                            Repeat sections
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '12px',
                                                lineHeight: '16px',
                                                color: '#6b7280'
                                            }}
                                        >
                                            Create sub-sections to summarize each result from a study or endpoint
                                        </Typography>
                                    </Box>
                                    <ArtosToggle
                                        checked={repeatSection}
                                        onChange={() => handleRepeatToggle()}
                                        sx={{ flexShrink: 0 }}
                                        showIcons={true}
                                        activeColor="#4F46E5"
                                        inactiveColor="#E5E7EB"
                                    />
                                </Box>

                                {/* Data Instructions Field - shown when Repeat Sections is enabled */}
                                {repeatSection && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <Typography
                                            sx={{
                                                fontSize: '12px',
                                                fontWeight: 500,
                                                color: '#374151'
                                            }}
                                        >
                                            Data Instructions
                                        </Typography>
                                        <TextField
                                            multiline
                                            rows={3}
                                            value={localSection?.data_instructions || ''}
                                            onChange={(e) => {
                                                setLocalSection({ ...localSection, data_instructions: e.target.value })
                                                debouncedUpdate({ data_instructions: e.target.value })
                                            }}
                                            placeholder="Enter instructions for how to handle the data when repeating sections..."
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    fontSize: '13px',
                                                    lineHeight: '20px',
                                                    color: '#374151',
                                                    backgroundColor: '#ffffff',
                                                    '& fieldset': {
                                                        borderColor: '#D1D5DB',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#9CA3AF',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#4F46E5',
                                                    }
                                                },
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '10px 12px',
                                                }
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: '11px',
                                                color: '#6B7280',
                                                fontStyle: 'italic'
                                            }}
                                        >
                                            Provide specific instructions for how data should be grouped, filtered, or organized when creating repeated sections.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* Allowed Data Rules Multi-select OR Use Source Data Toggle */}
                            {section?.allowed_data_rules !== undefined ? (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box sx={{ width: '100%' }}>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                lineHeight: '20px',
                                                fontWeight: 500,
                                                color: '#374151',
                                                mb: '4px'
                                            }}
                                        >
                                            Allow data rules
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '12px',
                                                lineHeight: '16px',
                                                color: '#6b7280',
                                                mb: '4px'
                                            }}
                                        >
                                            Include data-driven results in the section
                                        </Typography>
                                        <FormControl sx={{ minWidth: 300, flexShrink: 0, width: '100%' }} fullWidth>
                                            <Select
                                                multiple
                                                value={allowedDataRules}
                                                onChange={handleAllowedDataRulesChange}
                                                displayEmpty
                                                renderValue={(selected) => {
                                                    if (selected.length === 0) {
                                                        return <Typography sx={{ color: '#9CA3AF', fontSize: '12px' }}>Select data rules</Typography>
                                                    }
                                                    return (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {selected.map((value) => (
                                                                <Chip
                                                                    key={value}
                                                                    label={value === 'listings_files' ? 'Use listings files' : value === 'clinical_table' ? 'Add clinical table' : 'Summarise non-clinical report'}
                                                                    size="small"
                                                                    onDelete={(e) => {
                                                                        e.stopPropagation()
                                                                        const newValue = allowedDataRules.filter(item => item !== value)
                                                                        setAllowedDataRules(newValue)

                                                                        // If removing the last allowed data rule, set content_mode back to 'background'
                                                                        if (newValue.length === 0) {
                                                                            console.log('❌ Last allowed data rule removed - setting content_mode to background')
                                                                            setUseSourceData(false)
                                                                            debouncedUpdate({
                                                                                allowed_data_rules: newValue,
                                                                                content_mode: 'background'
                                                                            })
                                                                        } else {
                                                                            debouncedUpdate({ allowed_data_rules: newValue })
                                                                        }
                                                                    }}
                                                                    onMouseDown={(e) => {
                                                                        // Prevent the Select from opening when clicking the delete icon
                                                                        if (e.target.closest('.MuiChip-deleteIcon')) {
                                                                            e.stopPropagation()
                                                                        }
                                                                    }}
                                                                    deleteIcon={<CloseIcon sx={{ fontSize: '14px' }} />}
                                                                    sx={{
                                                                        backgroundColor: '#EEF2FF',
                                                                        color: '#4F46E5',
                                                                        fontSize: '12px',
                                                                        height: '22px',
                                                                        '& .MuiChip-deleteIcon': {
                                                                            color: '#4F46E5',
                                                                            '&:hover': {
                                                                                color: '#3730A3'
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    )
                                                }}
                                                sx={{
                                                    width: '100%',
                                                    backgroundColor: '#FFFFFF',
                                                    fontSize: '12px',
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: '#E5E7EB',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: '#D1D5DB',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#1F477D',
                                                        },
                                                    },
                                                    '& .MuiSelect-select': {
                                                        padding: '8px 12px',
                                                        minHeight: '32px',
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            borderRadius: '6px',
                                                            border: '1px solid #E5E7EB',
                                                            boxShadow: '0 1px 2px 0 rgb(0, 0, 0, 0.05)',
                                                            maxHeight: '200px',
                                                            marginTop: '4px',
                                                        },
                                                    },
                                                }}
                                            >
                                                <MenuItem value="listings_files">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        <Typography sx={{ fontSize: '12px' }}>Use listings files</Typography>
                                                    </Box>
                                                </MenuItem>
                                                <MenuItem value="clinical_table">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        <Typography sx={{ fontSize: '12px' }}>Add clinical table</Typography>
                                                    </Box>
                                                </MenuItem>
                                                <MenuItem value="non_clinical_report">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        <Typography sx={{ fontSize: '12px' }}>Summarise non-clinical report</Typography>
                                                    </Box>
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                lineHeight: '20px',
                                                fontWeight: 500,
                                                color: '#374151',
                                                mb: '4px'
                                            }}
                                        >
                                            Use special data
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '12px',
                                                lineHeight: '16px',
                                                color: '#6b7280'
                                            }}
                                        >
                                            Use 'special' data, including TFLs or non-clinical reports as sources.
                                        </Typography>
                                    </Box>
                                    <ArtosToggle
                                        checked={useSourceData}
                                        onChange={() => handleUseSourceDataToggle()}
                                        sx={{ flexShrink: 0 }}
                                        showIcons={true}
                                        activeColor="#4F46E5"
                                        inactiveColor="#E5E7EB"
                                    />
                                </Box>
                            )}

                            {/* Assist with AI Toggle */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: '14px',
                                            lineHeight: '20px',
                                            fontWeight: 500,
                                            color: '#374151',
                                            mb: '4px'
                                        }}
                                    >
                                        AI tool use
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                            lineHeight: '16px',
                                            color: '#6b7280'
                                        }}
                                    >
                                        Allow Artos to pick tools beyond those listed in the template. Useful for sections with multiple sources and advanced reasoning.
                                    </Typography>
                                </Box>
                                <ArtosToggle
                                    checked={assistWithAI}
                                    onChange={() => handleAssistToggle()}
                                    sx={{ flexShrink: 0 }}
                                    showIcons={true}
                                    activeColor="#4F46E5"
                                    inactiveColor="#E5E7EB"
                                />
                            </Box>
                        </Box>
                    )}
                    {sectionFormat === 1 && (
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                gap: '12px',
                                alignItems: 'flex-start',
                                width: '100%'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    color: '#374151',
                                    fontWeight: 600,
                                    flexShrink: 0
                                }}
                            >
                                Customize Format
                            </Typography>
                            <TextField
                                multiline
                                minRows={1}
                                fullWidth
                                placeholder="e.g. 'Section 1: Summary of the section's content'"
                                value={localSection.template_instructions || ''}
                                onChange={(e) => handleCustomSectionChange(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: '12px',
                                        backgroundColor: '#FFFFFF',
                                        '& fieldset': {
                                            borderColor: '#e5e7eb'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#d1d5db'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#3b82f6'
                                        },
                                        padding: '6px 12px',
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        fontSize: '12px',
                                        lineHeight: '20px',
                                        '&::placeholder': {
                                            color: '#9ca3af',
                                            opacity: 1
                                        }
                                    }
                                }}
                            />
                        </Box>
                    )}
                </Box>
                {/* Tools Section */}
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    color: '#111827'
                                }}
                            >
                                Tools · {(localSection.rules || []).length}
                            </Typography>
                            {/* <InfoOutlinedIcon sx={{ fontSize: '18px', color: '#9ca3af' }} /> */}
                        </Box>
                    </Box>

                    {/* Tools List */}
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '16px',
                            padding: '16px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            backgroundColor: '#ffffff',
                            minWidth: 0,
                            width: '100%',
                            boxSizing: 'border-box'
                        }}
                    >
                        {(localSection.rules || []).map((rule, index) => (
                            <Box 
                                key={rule.id || index}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '4px'
                                }}
                            >
                                {/* Tool Header */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: '#374151'
                                        }}
                                    >
                                        Tool {index + 1}
                                    </Typography>
                                    <IconButton
                                        onClick={() => handleDeleteRule(index)}
                                        sx={{
                                            padding: '4px',
                                            color: '#9ca3af',
                                            '&:hover': {
                                                color: '#ef4444',
                                                backgroundColor: '#fee2e2'
                                            }
                                        }}
                                    >
                                        <DeleteIcon sx={{ fontSize: '18px' }} />
                                    </IconButton>
                                </Box>
                                {/* Tool Content */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    border: '1px solid #e5e7eb', 
                                    borderRadius: '8px', 
                                    padding: '16px',
                                    minWidth: 0,
                                    width: '100%',
                                    boxSizing: 'border-box'
                                }}>
                                    {/* Rule Type */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {/* Rule Type Selector */}
                                        <Box 
                                            sx={{ 
                                                display: 'flex', 
                                                gap: '12px',
                                                alignItems: 'center',
                                                width: '100%',
                                                minWidth: 0
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: '12px',
                                                    color: '#6b7280',
                                                    width: '30%',
                                                    flexShrink: 0
                                                }}
                                            >
                                                Rule
                                            </Typography>
                                            <Dropdown
                                                uniqueKey='value'
                                                value={(() => {
                                                    // If ui_rule_type is set, use it
                                                    if (rule.ui_rule_type) return rule.ui_rule_type

                                                    // Handle copy_paste display based on rule_mode
                                                    if (rule.rule_type === 'copy_paste') {
                                                        return rule.rule_mode === 'dynamic' ? 'modified_copy_paste' : 'copy_paste'
                                                    }

                                                    // Handle template_text display based on rule_mode
                                                    if (rule.rule_type === 'template_text' && rule.rule_mode === 'dynamic') {
                                                        return 'fill_in_blank'
                                                    }

                                                    return rule.rule_type || 'copy_paste'
                                                })()}
                                                options={ruleTypeOptions}
                                                onChange={(e) => handleRuleChange(index, 'rule_type', e.target.value)}
                                                fontSize='12px'
                                            />
                                        </Box>

                                        {/* Source Dropdown for Copy/Paste rules */}
                                        {/* TODO: Add back when we have this working */}
                                        {/* {(rule.rule_type === 'copy_paste' || rule.rule_type === 'modified_copy_paste' || rule.rule_type === 'summarize_content') && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: '12px',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                    minWidth: 0
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '12px',
                                                        color: '#6b7280',
                                                        width: '30%',
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    {rule.rule_type === 'summarize_content' ? 'Summarize from' : 'Copy from'}
                                                </Typography>
                                                <Dropdown
                                                    uniqueKey='value'
                                                    value={(sourceMappings && sourceMappings[rule.id || `rule_${index}`]) || ''}
                                                    options={[
                                                        { value: '', label: 'Select source document' },
                                                        { value: 'Protocol', label: 'Protocol' },
                                                        { value: 'SAP', label: 'SAP' },
                                                        { value: 'TFL', label: 'TFL' }
                                                    ]}
                                                    onChange={(e) => handleSourceChange(index, e.target.value)}
                                                    fontSize='12px'
                                                />
                                            </Box>
                                        )} */}

                                        {/* Instructions Field for Copy/Paste rules */}
                                        {(rule.rule_type === 'copy_paste' || rule.rule_type === 'modified_copy_paste') && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: '12px',
                                                    alignItems: 'flex-start',
                                                    width: '100%',
                                                    minWidth: 0
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '12px',
                                                        color: '#6b7280',
                                                        width: '30%',
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    Instructions
                                                </Typography>
                                                <TextField
                                                    multiline
                                                    minRows={1}
                                                    fullWidth
                                                    placeholder="Tell us what information to be copied and pasted"
                                                    value={(() => {
                                                        // Extract just the instructions part without the "Copy from X" prefix
                                                        const instructions = rule.user_instructions || rule.instructions || ''
                                                        return instructions.replace(/^Copy from (Protocol|SAP|TFL)\.?\s*/i, '')
                                                    })()}
                                                    onChange={(e) => {
                                                        // When typing in instructions, preserve the source prefix if it exists
                                                        const sourcePrefix = rule.source_type ? `Copy from ${rule.source_type}. ` : ''
                                                        handleRuleChange(index, 'user_instructions', sourcePrefix + e.target.value)
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            fontSize: '12px',
                                                            backgroundColor: '#FFFFFF',
                                                            '& fieldset': {
                                                                borderColor: '#e5e7eb'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#d1d5db'
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#3b82f6'
                                                            },
                                                            padding: '6px 12px',
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            fontSize: '12px',
                                                            lineHeight: '20px',
                                                            '&::placeholder': {
                                                                color: '#9ca3af',
                                                                opacity: 1
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Box>
                                        )}

                                        {/* Instructions Field for non-Copy/Paste rules */}
                                        {rule.rule_type !== 'copy_paste' && rule.rule_type !== 'modified_copy_paste' && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: '12px',
                                                alignItems: 'flex-start',
                                                width: '100%',
                                                minWidth: 0
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: '12px',
                                                    color: '#6b7280',
                                                    width: '30%',
                                                    flexShrink: 0
                                                }}
                                            >
                                                    Instructions
                                                </Typography>
                                            <TextField
                                                multiline
                                                minRows={1}
                                                fullWidth
                                                placeholder="Tell us how you'd like information generated"
                                                value={rule.user_instructions || rule.instructions || ''}
                                                onChange={(e) => handleRuleChange(index, 'user_instructions', e.target.value)}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        fontSize: '12px',
                                                        backgroundColor: '#FFFFFF',
                                                        '& fieldset': {
                                                            borderColor: '#e5e7eb'
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: '#d1d5db'
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#3b82f6'
                                                        },
                                                        padding: '6px 12px',
                                                    },
                                                    '& .MuiOutlinedInput-input': {
                                                        fontSize: '12px',
                                                        lineHeight: '20px',
                                                        '&::placeholder': {
                                                            color: '#9ca3af',
                                                            opacity: 1
                                                        }
                                                    }
                                                }}
                                            />
                                            </Box>
                                        )}
                                        {/* Modify with AI Toggle */}
                                        {/* <Box 
                                            sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center',
                                                width: '100%',
                                                minWidth: 0
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '12px',
                                                        lineHeight: '16px',
                                                        color: '#6b7280'
                                                    }}
                                                >
                                                    Optimize with AI
                                                </Typography>
                                                <Tooltip 
                                                    title="Let AI use examples, context from other parts of the document, and more to optimize document content."
                                                    arrow
                                                    placement="right"
                                                >
                                                    <InfoOutlinedIcon sx={{ fontSize: '16px', color: '#9ca3af' }} />
                                                </Tooltip>
                                            </Box>
                                            {(() => {
                                                const fixedModeRules = ['literal_content', 'template_text', 'copy_paste']
                                                const isFixedMode = fixedModeRules.includes(rule.rule_type)
                                                return (
                                                    <Tooltip
                                                        title={isFixedMode ? "This rule type requires fixed mode" : ""}
                                                        placement="left"
                                                    >
                                                        <span>
                                                            <ArtosToggle
                                                                checked={rule.rule_mode === 'dynamic'}
                                                                onChange={() => handleModifyWithAIToggle(index)}
                                                                showIcons={true}
                                                                activeColor="#4F46E5"
                                                                inactiveColor="#E5E7EB"
                                                                disabled={isFixedMode}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                )
                                            })()}
                                        </Box> */}
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                        {/* Add Tool Button */}
                        <SecondaryButtonFactory
                            onClick={handleAddRule}
                            size='m'
                            bold={true}
                            startIcon={<AddIcon />}
                            disabled={ruleTypeOptions.length === 0}
                            sx={{
                                paddingLeft: '12px',
                            }}
                        >
                            New tool
                        </SecondaryButtonFactory>
                    </Box>
                </Box>
            </>
            ) : (
                /* Empty State when content is disabled */
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '60px 20px',
                        minHeight: '400px',
                        width: '100%'
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '24px'
                        }}
                    >
                        {/* Icon container */}
                        <EmptyContentIcon 
                            style={{
                                width: '135px',
                                height: '105px'
                            }}
                        />
                        
                        {/* Text */}
                        <Typography
                            sx={{
                                fontSize: '20px',
                                color: '#374151',
                                fontWeight: 600,
                                textAlign: 'center',
                                lineHeight: '20px'
                            }}
                        >
                            No content will be shown for this section
                        </Typography>
                    </Box>
                </Box>
            )}
            
            {/* Save Button - Always visible, enabled only when there are changes */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <AsynchronousActionButton
                    onClick={handleSave}
                    disabled={!hasChanges}
                    loading={loading}
                    startIcon={ loading ? null: <SaveIcon /> }
                    sx={{
                        width: '200px',
                        height: '44px',
                        backgroundColor: hasChanges ? '#1F477D' : '#E5E7EB',
                        color: hasChanges ? '#FFFFFF' : '#6B7280',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: hasChanges ? '#163A63' : '#E5E7EB'
                        },
                        '&:disabled': {
                            backgroundColor: '#E5E7EB',
                            color: '#6B7280'
                        }
                    }}
                >
                    { loading ? "" : "Save changes" }
                </AsynchronousActionButton>
            </Box>
        </Box>
    )
}